import React from 'react';

import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {P} from '../../../layout/Text/Text';
import {InfoPopup} from '../../InfoPopup/InfoPopup';
import {Hints} from '../Hints/Hints';
import {TagCheckbox} from '../TagCheckbox/TagCheckbox';

import {useTagCloud} from './useTagCloud';

import type {Option} from '../../../../utils/childTypes';
import type {ValidationFunc} from '@nfq/react-form';

/**
 * Interface defining the properties for a component that handles a specific field.
 * It includes the necessary information for field identification, initial value, display, and testing.
 */
export interface ComponentProps {
    /**
     * The `fieldName` is used as an identifier for the field within the component.
     * This name should be unique and descriptive, indicating the purpose or content of the field.
     */
    fieldName: string;
    /**
     * An array of hint messages to display below the dropdown input.
     * These hints provide additional context or guidance to the user about the expected input
     * or usage of the dropdown component.
     *
     * @default undefined
     */
    hints?: string[];
    /**
     * The initial value set for the field when the component is first rendered.
     * This is an array of `AvailableChecks`, indicating the default checked states or selections.
     *
     * @default []
     */
    initialValue?: string[];
    /**
     * Determines whether the tag cloud allows only a single selection.
     * When set to true, users can select only one tag at a time; otherwise, multiple selections are allowed.
     *
     * @default false
     */
    isSingle?: boolean;
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    keepOnUnmount?: boolean;
    /**
     * The text label associated with the field.
     * This label is displayed to the user and should clearly describe the field's purpose or content.
     */
    label: string;
    /**
     * The tags that will be displayed in the tag cloud.
     */
    options: Option[];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'TradesCloud'
     */
    testId?: string;
    /**
     * Optional tooltip text to provide additional information about the tag cloud.
     * This text is displayed in a popup when the user hovers over the info icon next to the label.
     */
    tooltipText?: string;
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * A tag selection component that renders a list of checkboxes for tagging purposes.
 * This component allows users to select multiple tags by checking or unchecking corresponding checkboxes.
 * It manages the selected tags using the `useTagCloud` hook, ensuring that changes are reflected in the form state.
 *
 * @param props              The component props.
 * @param props.fieldName    The name of the form field associated with the tag selection.
 * @param props.initialValue An array of initially selected tag values (default: `[]`).
 * @param props.label        The label describing the tag selection field.
 * @param props.tags         An array of available tags, each containing a label and a tag value.
 * @param props.testId       A unique test identifier for the component (default: `'TagCloud'`).
 * @returns A React component rendering a group of tag checkboxes.
 *
 * @example
 * ```tsx
 * <TagCloud
 *   fieldName="categories"
 *   initialValue={["tag1"]}
 *   label="Select Tags"
 *   tags={[{ label: "Tag 1", tag: "tag1" }, { label: "Tag 2", tag: "tag2" }]}
 * />
 * ```
 */
const TagCloud = observer(({
    fieldName,
    hints = [],
    initialValue = [],
    isSingle = false,
    keepOnUnmount = false,
    label,
    options,
    testId = 'TagCloud',
    tooltipText,
    validation = () => null
}: ComponentProps) => {
    const {error, handleChange, value} = useTagCloud(fieldName, initialValue, isSingle, keepOnUnmount, validation);

    return (
        <Wrapper aria-label={label}>
            <LabelWrapper data-cy={`${testId}-label`}>
                {label}
                {tooltipText && (
                    <InfoPopup testId={`${testId}-info-popup`}>
                        <P $size="small" data-cy={`${testId}-info-text`}>{tooltipText}</P>
                    </InfoPopup>
                )}
            </LabelWrapper>
            <Spacer y={2} />
            <TagCheckboxWrapper>
                {options.map(({color: tabActiveBorderColor, label: tagLabel, value: tagValue}) => (
                    <TagCheckbox
                        key={tagValue}
                        activeBorderColor={tabActiveBorderColor}
                        fieldName={fieldName}
                        fieldValue={tagValue}
                        formValue={value}
                        isSingle={isSingle}
                        label={tagLabel}
                        testId={`${testId}-${tagValue}`}
                        onChange={handleChange}
                    />
                ))}
            </TagCheckboxWrapper>
            {(error !== null && error.length > 0) && (
                <Hints baseTestId={testId} hints={error} hintType="error" paddingLeft={0} />
            )}
            {(error === null || error.length === 0) && <Hints baseTestId={testId} hints={hints} paddingLeft={0} />}
        </Wrapper>
    );
});

TagCloud.displayName = 'TagCloud';

export {TagCloud};

const Wrapper = styled.div`
    width: 100%;
`;

const LabelWrapper = styled(P)`
    display: flex;
    gap: ${spacing(2)};
`;

const TagCheckboxWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(2)};
    width: 100%;
`;